import { useContext } from 'react';
import { FeatureGroup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { MapPin, StationType, Todo } from '../schema';
import StationMarker from './StationMarker';
import { MapContext, useEventMap, useEventTodo } from '../context';
import { db } from '../instant';
import { id } from '@instantdb/react';
import { Box, CircularProgress, Typography } from '@mui/material';

interface MarkerInfo {
    station: StationType;
    position: L.LatLng;
}

interface Props {
    bounds: L.LatLngBounds;
    magnification: number;
}

export default function StationMarkerGroup(props: Props) {
    const {
        eventId,
        mapNumber,
        cleanupMode,
        currentStation,
        setCurrentStation,
        currentTodoType,
        setCurrentTodoType,
        toggleTodoMode,
        selectedStationTypeIds,
        todoOnlyFilter,
        notesOnlyFilter,
    } = useContext(MapContext);
    const { map, isLoading, error } = useEventMap(eventId, mapNumber);
    const { todo: todos } = useEventTodo(eventId);

    const { data: stationTypesData } = db.useQuery({ stationTypes: {} });

    const overlayWidth = props.bounds.getEast();
    const overlayHeight = props.bounds.getNorth();

    const addPin = async (newMarker: MarkerInfo) => {
        if (!map) {
            throw new Error('no map');
        }

        const pinId = id();
        const mapPin: Omit<MapPin, 'stationType'> = {
            id: pinId,
            // Convert to fractional coordinates
            x: newMarker.position.lng / overlayWidth,
            y: newMarker.position.lat / overlayHeight,
            cleanedUp: false,
            notes: '',
        };

        // Match normal map-edit placement behavior: one marker per station selection.
        setCurrentStation(null);

        const txs: any[] = [db.tx.mapPins[pinId].update(mapPin).link({ map: map.id }).link({ stationType: newMarker.station.id })];

        const shouldExitTodoMode =
            !!currentTodoType && currentTodoType.interactionType === 'place-new' && currentTodoType.name !== 'place-bin';
        if (currentTodoType && currentTodoType.interactionType === 'place-new') {
            const todoId = id();
            const todo = {
                id: todoId,
                completed: false,
                created_at: new Date().toISOString(),
                completed_at: new Date().toISOString(),
                notes: '',
            };
            txs.push(
                db.tx.todos[todoId].update(todo).link({ event: eventId }).link({ todoType: currentTodoType.id }).link({ mapPin: pinId })
            );
            if (shouldExitTodoMode) {
                setCurrentTodoType(null);
            }
        }

        await db.transact(txs);
        console.log('saved!');

        if (shouldExitTodoMode) {
            toggleTodoMode();
        }
    };

    const deletePin = async (pinId: string) => {
        await db.transact(db.tx.mapPins[pinId].delete());
    };

    useMapEvents({
        click: async ev => {
            const isStageType = currentTodoType?.name === 'pickup-needed' || currentTodoType?.iconName === 'square-with-x.svg';

            let stationToPlace = currentStation;

            if (isStageType && !currentStation && stationTypesData?.stationTypes) {
                const stagingStation = stationTypesData.stationTypes.find(st => st.name === 'Staging Area');
                if (stagingStation) {
                    stationToPlace = stagingStation;
                }
            }

            if (!stationToPlace) {
                return;
            }

            const newPosition = ev.latlng;

            if (props.bounds.contains(newPosition)) {
                const newMarker: MarkerInfo = {
                    station: stationToPlace,
                    position: newPosition,
                };
                await addPin(newMarker);
            }
        },
    });

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" padding={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Typography color="error">Error loading map data: {error.message}</Typography>;
    }

    let mapPins = map?.pins || [];
    if (cleanupMode) {
        mapPins = mapPins.filter(
            // Hide cleaned up stations
            pin => !pin.cleanedUp
        );
    }

    const todoPinIds = new Set<string>();
    const todoPinMap = new Map<string, Todo>();
    const completedTodoPinIds = new Set<string>();
    if (todos && todos.length > 0) {
        todos.forEach((todo: Todo) => {
            if (todo.mapPin?.id) {
                if (!todo.completed) {
                    todoPinIds.add(todo.mapPin.id);
                    todoPinMap.set(todo.mapPin.id, todo);
                } else {
                    completedTodoPinIds.add(todo.mapPin.id);
                }
            }
        });
    }

    mapPins = mapPins.filter((pin: MapPin) => {
        const isStagingArea = pin.stationType?.name === 'Staging Area';
        if (isStagingArea && completedTodoPinIds.has(pin.id)) {
            return false;
        }
        return true;
    });

    if (selectedStationTypeIds.size > 0) {
        mapPins = mapPins.filter((pin: MapPin) => pin.stationType?.id && selectedStationTypeIds.has(pin.stationType.id));
    }

    if (todoOnlyFilter) {
        mapPins = mapPins.filter((pin: MapPin) => todoPinIds.has(pin.id));
    }

    if (notesOnlyFilter) {
        mapPins = mapPins.filter((pin: MapPin) => pin.notes && pin.notes.trim().length > 0);
    }

    return (
        <FeatureGroup>
            {mapPins
                .map((pin: MapPin) => ({
                    pin,
                    station: pin.stationType,
                }))
                .filter(({ station }: { station: StationType | undefined }) => station)
                .map(({ pin, station }: { pin: MapPin; station: StationType | undefined }) => {
                    // transform lat, lng into fractional coords
                    // note order: lat, lng = y, x
                    const position = new L.LatLng(overlayHeight * (pin.y as number), overlayWidth * (pin.x as number));

                    const marker: MarkerInfo = {
                        station: station as StationType,
                        position,
                    };

                    return (
                        <StationMarker
                            pin={pin}
                            station={marker.station}
                            position={marker.position}
                            key={pin.id}
                            isTodo={todoPinIds.has(pin.id)}
                            todo={todoPinMap.get(pin.id)}
                            onDelete={() => deletePin(pin.id).catch(console.error)}
                        />
                    );
                })}
        </FeatureGroup>
    );
}
